import * as _ from "lodash";
import { CONSTANT } from "../shared/constant";
import { callApi } from "./callApi";
import {TempFilterState} from "../interfaces/leagueFilter";
import {NewsFilterInterface, NewsTypeValue} from "../interfaces/news";

export const register = (data: any) => {
  let endpoint = "/auth/register";
  const method = "POST";
  return callApi(endpoint, method, data, "v1", true);
};

/**
 * Bước 1 đăng ký: xin mã xác nhận gửi tới email vừa khai.
 *
 * Chưa tạo tài khoản gì — máy chủ chỉ gửi mã. 422 kèm 'phoneAlreadyExists' /
 * 'emailAlreadyExists' nếu một trong hai đã có người dùng.
 */
export const requestRegisterOtp = (data: { phone: string; email: string }) => {
  return callApi("/auth/register/otp", "POST", data, "v1", true);
};

/** Bước 2: gõ mã. Đúng thì lượt đăng ký này được đánh dấu đã xác nhận. */
export const verifyRegisterOtp = (data: { email: string; otp: string }) => {
  return callApi("/auth/register/otp/verify", "POST", data, "v1", true);
};

export const login = (data: any) => {
  let endpoint = "/auth/login";
  const method = "POST";
  return callApi(endpoint, method, data, "v1", true);
};

/**
 * Đăng nhập bằng Google.
 *
 * Trả về một trong hai thân: thân đăng nhập quen thuộc ({ token, ... }) nếu
 * email đã có tài khoản, hoặc { needRegister: true, profile } nếu chưa - lúc đó
 * phải đi tiếp màn đăng ký để nhập số điện thoại.
 */
export const googleLogin = (idToken: string) => {
  return callApi("/auth/google", "POST", { idToken }, "v1", true);
};

/** Hoàn tất đăng ký bằng Google: số điện thoại + tên + ảnh. */
export const googleRegister = (data: {
  idToken: string;
  phone: string;
  name?: string;
  profilePicture?: string;
}) => {
  return callApi("/auth/google/register", "POST", data, "v1", true);
};

/**
 * Hỏi trước xem số điện thoại / email đã có ai dùng chưa.
 *
 * Trống (204) là dùng được; 422 kèm 'phoneAlreadyExists' / 'emailAlreadyExists'
 * là đã có người dùng. Để màn đăng ký báo trùng ngay ở bước nhập thông tin,
 * thay vì để người dùng đi tiếp sang bước chọn ảnh rồi mới báo - ở đó không còn
 * ô nào để sửa.
 */
export const checkRegisterAvailability = (data: { phone?: string; email?: string }) => {
  return callApi("/auth/register/check", "POST", data, "v1", true);
};

/** Bước 1 quên mật khẩu: xin mã OTP gửi về email đã đăng ký. */
export const requestPasswordOtp = (email: string) => {
  return callApi("/auth/password/forgot", "POST", { email }, "v1", true);
};

/** Bước 2 quên mật khẩu: mã OTP + mật khẩu mới. */
export const resetPasswordWithOtp = (data: { email: string; otp: string; password: string }) => {
  return callApi("/auth/password/reset", "POST", data, "v1", true);
};

/**
 * Cập nhật hồ sơ của chính mình.
 *
 * Chỉ gửi những trường thật sự đổi: DTO bên backend kiểm tra trùng số điện
 * thoại và email, gửi lại giá trị cũ là tự báo "đã tồn tại" với chính mình.
 */
export const updateMyProfile = (payload: {
  name?: string;
  phone?: string;
  email?: string;
}) => {
  return callApi("/auth/me", "PATCH", payload, "v1");
};

export const myProfile = () => {
  let endpoint = "/players/me";
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getMyCalibrationHistory = () => {
  let endpoint = "/auth/me/calibration/results";
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getUserById = (playerId: any) => {
  let endpoint = `/players/${playerId}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

/** Chưa đăng nhập thì đọc bản công khai — callApi trả false khi endpoint đòi
 *  token mà không có, tức là trang chủ và danh sách giải sẽ trống trơn. */
const isAnonymous = () => !localStorage.getItem("token");

export const getLeagues = (data: any) => {
  const anonymous = isAnonymous();
  let endpoint = anonymous ? `/public/leagues?` : `/leagues?`;
  const method = "GET";
  let filter = _.cloneDeep(data)

  if (data.limit) {
    endpoint += `&limit=${filter.limit}`;
  } else {
    endpoint += `&limit=${CONSTANT.limit}`;
  }
  if (data.page) {
    endpoint += `&page=${filter.page}`;
  } else {
    endpoint += `&page=${filter.page}`;
  }
  if (data.provinceCode) {
    endpoint += `&provinceCode=${data.provinceCode}`;
  }
  if (data.leagueType) {
    endpoint += `&leagueType=${data.leagueType}`;
  }
  if (data.districtCode && data.districtCode.length > 0) {
    _.each(data.districtCode, (value, key) => {
      endpoint += `&districtCode[]=${value}`;
    })
  }
  if (data.venues && data.venues.length > 0) {
    _.each(data.venues, (value, key) => {
      endpoint += `&venues[]=${value}`;
    })
  }
  if (data.filter) {
    endpoint += `&filter=${data.filter}`;
  }
  // Bản công khai không biết "của tôi" là ai, nên bỏ hẳn tham số thay vì gửi
  // lên rồi nhận danh sách sai.
  if (data.onlyMyLeagues && !anonymous) {
    endpoint += `&onlyMyLeagues=true`;
  }
  return callApi(endpoint, method, "", "v1", anonymous ? true : undefined);
};

export const getMiniGame = (data: any) => {
  let endpoint = `/miniGames?`;
  const method = "GET";
  if (data.limit) {
    endpoint += `&limit=${data.limit}`;
  } else {
    endpoint += `&limit=${CONSTANT.limit}`;
  }
  if (data.page) {
    endpoint += `&page=${data.page}`;
  } else {
    endpoint += `&page=${data.page}`;
  }
  return callApi(endpoint, method, "", "v1");
};

export const getLeagueById = (id: any) => {
  let endpoint = isAnonymous() ? `/public/leagues/${id}` : `/leagues/${id}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1", isAnonymous() ? true : undefined);
};

export const getCheckinListByLeagueId = (id: any, limit = 50, page = 1) => {
  let endpoint = `/leagues/${id}/checkin`;
  endpoint += `?limit=${limit}&page=${page}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getLeaguePlayersById = (id: any, limit = 50, page = 1) => {
  let endpoint = isAnonymous() ? `/public/leagues/${id}/participants` : `/leagues/${id}/participants`;
  endpoint += `?limit=${limit}&page=${page}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1", isAnonymous() ? true : undefined);
};

export const getMatchCountInLeagueByCurrentWeek = (
  leagueId: string | null | undefined,
  playerId: string
) => {
  let endpoint = `/leagues/${leagueId}/played/${playerId}/week`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getExperts = (limit = 50, page = 1) => {
  // Endpoint riêng chứ không phải /players?onlyExpert=true: chuyên gia công bố
  // thông tin liên hệ, còn danh sách cơ thủ chung thì không được kèm theo.
  let endpoint = "/players/experts";
  const method = "GET";
  let params = {
    limit: limit,
    page: page,
  };
  return callApi(endpoint, method, params, "v1");
};

/**
 * Số điện thoại / email của một cơ thủ.
 *
 * Tách khỏi getUserById vì backend không còn kèm dữ liệu cá nhân vào hồ sơ:
 * endpoint này kiểm tra người xem có lý do chính đáng không (chính chủ, chuyên
 * gia/trọng tài, hoặc cùng chung một giải). Trả 403 thì coi như không có số.
 */
export const getPlayerContact = async (playerId: any) => {
  const endpoint = `/players/${playerId}/contact`;
  const rsp = await callApi(endpoint, "GET", "", "v1");
  // Backend bọc payload trong { data } khi nó không có khoá phân trang. Bóc ở
  // đây để nơi gọi chỉ việc đọc .phone — chính chỗ này từng làm nút "LIÊN HỆ"
  // không bao giờ chạy: nó đọc rsp.phone trên object đã bị bọc.
  return rsp?.data ?? rsp;
};

/**
 * Bảng xếp hạng cơ thủ. Backend chưa có endpoint xếp hạng riêng, nên dùng
 * /players rồi sắp xếp theo điểm ở client. Khi có API BXH thật (WNT/WPA),
 * thay thân hàm này — component không phải sửa.
 */
/**
 * Cơ thủ xếp theo điểm giảm dần.
 *
 * Việc sắp xếp là của backend (/players đã ORDER BY score DESC) — sắp lại ở đây
 * chỉ sắp được đúng trang vừa tải, nên nếu backend trả thứ tự tuỳ ý thì "top 3"
 * vẫn là 3 người bất kỳ. Giữ lại phép sắp cho chắc: khi backend đã đúng thì nó
 * không đổi gì.
 */
export const getTopPlayers = async (limit = 50, page = 1) => {
  const anonymous = isAnonymous();
  const endpoint = anonymous ? "/public/players" : "/players";
  const method = "GET";
  const rsp = await callApi(endpoint, method, { limit, page }, "v1", anonymous ? true : undefined);
  if (!rsp || !Array.isArray(rsp.data)) return rsp;
  const sorted = [...rsp.data].sort(
    (a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)
  );
  return { ...rsp, data: sorted };
};

export const getRankGroupClass = (params: any = {}) => {
  let endpoint = "/ranks/groups";
  if (params) {
    endpoint = AppendGetRequestParameter(endpoint, params);
  }
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getCalibrationTypeList = () => {
  let endpoint = "/calibrations";
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const calibratePlayer = (data: any) => {
  let endpoint = "/auth/expert/calibrate";
  const method = "POST";
  return callApi(endpoint, method, data, "v1");
};

export const calibratePlayerLevel = (data: any) => {
  let endpoint = "/experts/calibrate/" + data.playerId;
  delete data.playerId;
  const method = "POST";
  return callApi(endpoint, method, data, "v1");
};

export const joinLeagueById = (leagueId: string) => {
  let endpoint = "/leagues/" + leagueId;
  const method = "POST";
  return callApi(endpoint, method, {}, "v1");
};

/**
 * Trạng thái đăng ký của chính mình trong một giải bất kỳ.
 *
 * Dùng chung cho mọi thể thức: giải Swiss trả về đúng dữ liệu của trang Swiss,
 * các thể thức còn lại trả { registered, state, requiresFee, feePaid, fee }
 * với state là not_joined | waiting_fee | joined.
 */
export const getMyLeagueStatus = (leagueId: string) => {
  let endpoint = "/leagues/" + leagueId + "/me";
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

/**
 * Người chơi nộp ảnh biên lai chuyển khoản lệ phí.
 *
 * `bill` là base64 THUẦN - DTO bên backend gác bằng @IsBase64() nên còn tiền
 * tố `data:image/...;base64,` là bị 422. Backend lưu đúng MỘT file cho mỗi
 * (giải, cơ thủ) nên nộp lại là ghi đè ảnh cũ.
 */
export const uploadFeeBill = (leagueId: string, bill: string) => {
  let endpoint = "/leagues/" + leagueId + "/fee-bill";
  const method = "POST";
  return callApi(endpoint, method, { bill }, "v1");
};

export const checkinLeagueById = (leagueId: string) => {
  let endpoint = "/leagues/" + leagueId + "/checkin";
  const method = "POST";
  return callApi(endpoint, method, {}, "v1");
};

export const checkoutLeagueById = (leagueId: string) => {
  let endpoint = "/leagues/" + leagueId + "/checkout";
  const method = "POST";
  return callApi(endpoint, method, {}, "v1");
};

const AppendGetRequestParameter = (url: string, params: any) => {
  if (url.indexOf("?") === -1) {
    url += "?";
  }
  if (_.isObject(params)) {
    _.forOwn(params, (val, key) => {
      url += `${key}=${val}`;
    });
  }
  return url;
};

export const getPublicLeagueById = (id: any) => {
  let endpoint = `/public/leagues/${id}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1", true);
};

export const getLeagueMatches = (id: any, limit = 50, page = 1) => {
  let endpoint = `/leagues/${id}/matches`;
  endpoint += `?limit=${limit}&page=${page}&onlyMyMatches=true`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getPlayingLeagueMatches = (id: any, limit = 50, page = 1) => {
  let endpoint = `/matches/playing`;
  endpoint += `?limit=${limit}&page=${page}&leagueId=${id}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getPlayerMatches = (id: any, limit = 50, page = 1) => {
  let endpoint = `/leagues/${id}/matches`;
  endpoint += `?limit=${limit}&page=${page}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getPlayerInLeagueData = (leagueId: any, playerId: string) => {
  let endpoint = `/leagues/${leagueId}/participants/${playerId}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const makeDraftMatch = (leagueId: any, competitorId: any) => {
  let endpoint = "/leagues/" + leagueId + "/players/" + competitorId;
  const method = "POST";
  return callApi(endpoint, method, { status: "DRAFT" }, "v1");
};

export const makeChallenger = (matchId: string) => {
  let endpoint = `/matches/${matchId}`;
  const method = "POST";
  return callApi(endpoint, method, { status: "WAITING" }, "v1");
};

export const acceptChallenger = (matchId: string) => {
  let endpoint = `/matches/${matchId}`;
  const method = "POST";
  return callApi(endpoint, method, { status: "ACCEPTED" }, "v1");
};

export const denyChallenger = (matchId: string) => {
  let endpoint = `/matches/${matchId}`;
  const method = "POST";
  return callApi(endpoint, method, { status: "DENIED" }, "v1");
};

export const changeMatchStatus = (matchId: string, status: string) => {
  let endpoint = `/matches/${matchId}`;
  const method = "POST";
  return callApi(endpoint, method, { status: status }, "v1");
};

export const confirmResult = (
  matchId: string,
  resultId: string,
  challengerId: string,
  acceptorId: string
) => {
  let endpoint = `/matches/${matchId}/results/confirm`;
  const method = "POST";
  return callApi(
    endpoint,
    method,
    { resultId: resultId, challengerId: challengerId, acceptorId: acceptorId },
    "v1"
  );
};

export const refuseResult = (matchId: string) => {
  let endpoint = `/matches/${matchId}/results/refuse`;
  const method = "POST";
  return callApi(endpoint, method, {}, "v1");
};

export const getProfileRankingHistory = (filter: any) => {
  let endpoint = `/players/me/rank-history`;
  const method = "GET";
  return callApi(
    endpoint,
    method,
    { limit: filter.limit, page: filter.page },
    "v1"
  );
};

export const getMatchById = (matchId: string) => {
  let endpoint = `/matches/${matchId}`;
  const method = "GET";
  return callApi(endpoint, method, {}, "v1");
};

export const getMatchResultByMatchId = (matchId: string) => {
  let endpoint = `/matches/${matchId}/results`;
  const method = "GET";
  return callApi(endpoint, method, {}, "v1");
};

export const getLeagueReferees = (leagueId: any, limit = 50, page = 1) => {
  let endpoint = `/leagues/${leagueId}/referees`;
  endpoint += `?limit=${limit}&page=${page}`;
  const method = "GET";
  return callApi(endpoint, method, {}, "v1");
};

export const submitMatch = (matchId: string, body: object) => {
  let endpoint = `/matches/${matchId}/results`;
  const method = "POST";
  return callApi(endpoint, method, body, "v1");
};

export const getPublicLeaguePlayer = (id: any, limit = 50, page = 1) => {
  let endpoint = `/public/leagues/${id}/participants`;
  endpoint += `?limit=${limit}&page=${page}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getPublicMatches = (id: any, limit = 50, page = 1) => {
  let endpoint = `/public/leagues/${id}/matches`;
  endpoint += `?limit=${limit}&page=${page}`;
  const method = "GET";
  return callApi(endpoint, method, "", "v1");
};

export const getMyMatch = (limit = 50, page = 1) => {
  let endpoint = `/matches`;
  const method = "GET";
  return callApi(
    endpoint,
    method,
    {
      limit: limit,
      page: page,
      onlyMyMatches: true,
    },
    "v1"
  );
};

export const leaveLeague = (leagueId: string, playerId: string) => {
  let endpoint = `/leagues/${leagueId}/participants/${playerId}`;
  const method = "DELETE";
  return callApi(endpoint, method, "", "v1");
};

export const getLeagueOpponentInfo = (leagueId: string, playerId: string) => {
  const endpoint = `/leagues/${leagueId}/bracket/${playerId}/opposite`;
  const method = "GET";

  return callApi(endpoint, method, "", "v1");
};

export const getMatchId = (leagueId: string, playerId: string) => {
  const endpoint = `/leagues/${leagueId}/players/${playerId}`;
  const method = "POST";

  return callApi(endpoint, method, { status: "DRAFT" }, "v1");
};

export const getMatchInfo = (matchId: string) => {
  const endpoint = `/matches/${matchId}`;
  const method = "GET";

  return callApi(endpoint, method, "", "v1");
};

export const getBracketLeagueInfo = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/bracket`;
  const method = "GET";

  return callApi(endpoint, method, "", "v1");
};

export const getUserHistory = (leagueId: string, playerId: string) => {
  const endpoint = `/leagues/${leagueId}/bracket/${playerId}/history`;
  const method = "GET";

  return callApi(endpoint, method, "", "v1");
};

export const loadProvince = () => {
  const endpoint = `/provinces`;
  const method = "GET";

  return callApi(endpoint, method, "", "v1", true);
}

export const loadVenueData = (provinceCode: string, districtCode: Array<String>) => {
  let endpoint = `/venues?`;
  if (provinceCode) {
    endpoint += `&provinceCode=${provinceCode}`;
  }
  if (districtCode) {
    _.each(districtCode, (value, key) => {
      endpoint += `&districtCode[]=${value}`;
    })
  }
  const method = "GET";

  return callApi(endpoint, method, "", "v1", true);
}

export const loadDistrictProvince = (provinceCode: string) => {
  const endpoint = `/provinces/${provinceCode}/districts`;
  const method = "GET";

  return callApi(endpoint, method, "", "v1", true);
}

/* ----------------------------- Thể thức Swiss -----------------------------
 * Bản công khai (/public/...) dùng khi chưa đăng nhập; bản có token trả thêm
 * phần của riêng người chơi (đăng ký, trận hiện tại).
 */

export const getSwissOverview = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/overview`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const getPublicSwissOverview = (leagueId: string) => {
  const endpoint = `/public/leagues/${leagueId}/swiss/overview`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

export const getSwissRounds = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/rounds`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const getPublicSwissRounds = (leagueId: string) => {
  const endpoint = `/public/leagues/${leagueId}/swiss/rounds`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

export const getSwissPlayoff = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/playoff`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const getPublicSwissPlayoff = (leagueId: string) => {
  const endpoint = `/public/leagues/${leagueId}/swiss/playoff`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

export const getSwissStandings = (leagueId: string, round?: number) => {
  let endpoint = `/public/leagues/${leagueId}/swiss/standings`;
  if (round !== undefined && round !== null) {
    endpoint += `?round=${round}`;
  }
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

export const getSwissMyStatus = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/me`;
  return callApi(endpoint, "GET", {}, "v1");
};

/** Những trận đang chờ tôi thao tác, gộp từ MỌI giải Swiss - nguồn cho carousel
 *  đầu trang chủ. Một lời gọi thay cho việc mở từng giải một để dò. */
export const getSwissMyActions = () => {
  const endpoint = `/leagues/swiss/my-actions`;
  return callApi(endpoint, "GET", {}, "v1");
};

/* Thông báo đẩy. Huỷ dùng POST chứ không DELETE: callApi bỏ thân của DELETE, mà
   mã token thì dài và không nên nằm trên URL. */
export const dangKyPushToken = (token: string, userAgent?: string) =>
  callApi('/push/tokens', "POST", { token, userAgent }, "v1");

export const huyPushToken = (token: string) =>
  callApi('/push/tokens/remove', "POST", { token }, "v1");

/**
 * Thông tin trận đấu trước khi bấm thách đấu — màn hiện ra sau khi quét QR.
 *
 * Đi qua cùng một lớp kiểm tra với chính lời thách đấu, nên lỗi ở đây (chưa bốc
 * cặp, không phải đối thủ vòng này, chưa đóng phí) đúng bằng lỗi lúc bấm nút.
 */
export const getSwissMatchup = (leagueId: string, opponentId: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/matchup/${opponentId}`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const joinSwissLeague = (leagueId: string, venueId?: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/join`;
  return callApi(endpoint, "POST", venueId ? { venueId } : {}, "v1");
};

/** Mở trận của cặp đã bốc sau khi quét QR đối thủ. Gọi lại nhiều lần vẫn trả về
 *  đúng trận đó, nên hai bên cùng quét cũng không tạo ra hai trận. */
export const swissChallenge = (leagueId: string, opponentId: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/challenge/${opponentId}`;
  return callApi(endpoint, "POST", {}, "v1");
};

/** Từ chối lời thách đấu đang chờ. Chỉ người ĐƯỢC MỜI gọi được. */
export const swissDeclineChallenge = (leagueId: string, opponentId: string) => {
  const endpoint = `/leagues/${leagueId}/swiss/challenge/${opponentId}/decline`;
  return callApi(endpoint, "POST", {}, "v1");
};

/* --------------------------- Thể thức Đấu kép ----------------------------
 * Cùng hình dạng với nhóm Swiss ở trên: bản công khai (/public/...) dùng khi
 * chưa đăng nhập, bản có token trả thêm phần của riêng người chơi.
 *
 * Đường dẫn là `de` chứ không phải `double-elimination`: đó là tiền tố backend
 * đặt (DoubleEliminationController), và nó ngắn nên URL trong log đọc được.
 */

export const getDeOverview = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/de/overview`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const getPublicDeOverview = (leagueId: string) => {
  const endpoint = `/public/leagues/${leagueId}/de/overview`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

export const getDeBracket = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/de/bracket`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const getPublicDeBracket = (leagueId: string) => {
  const endpoint = `/public/leagues/${leagueId}/de/bracket`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

export const getDeStandings = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/de/standings`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const getPublicDeStandings = (leagueId: string) => {
  const endpoint = `/public/leagues/${leagueId}/de/standings`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

/** Việc cần làm ở mọi giải Đấu kép - bản song song của getSwissMyActions. */
export const getDeMyActions = () => {
  const endpoint = `/leagues/de/my-actions`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const getDeMyStatus = (leagueId: string) => {
  const endpoint = `/leagues/${leagueId}/de/me`;
  return callApi(endpoint, "GET", {}, "v1");
};

/**
 * Thông tin trận đấu trước khi bấm thách đấu — màn hiện ra sau khi quét QR.
 *
 * Đi qua cùng một lớp kiểm tra với chính lời thách đấu, nên lỗi ở đây (không
 * phải đối thủ của cặp này, chưa đóng phí) đúng bằng lỗi lúc bấm nút.
 */
export const getDeMatchup = (leagueId: string, opponentId: string) => {
  const endpoint = `/leagues/${leagueId}/de/matchup/${opponentId}`;
  return callApi(endpoint, "GET", {}, "v1");
};

export const joinDeLeague = (leagueId: string, venueId?: string) => {
  const endpoint = `/leagues/${leagueId}/de/join`;
  return callApi(endpoint, "POST", venueId ? { venueId } : {}, "v1");
};

/** Mở trận của cặp đã bốc sau khi quét QR đối thủ. Gọi lại nhiều lần vẫn trả về
 *  đúng trận đó, nên hai bên cùng quét cũng không tạo ra hai trận. */
export const deChallenge = (leagueId: string, opponentId: string) => {
  const endpoint = `/leagues/${leagueId}/de/challenge/${opponentId}`;
  return callApi(endpoint, "POST", {}, "v1");
};

/** Từ chối lời thách đấu đang chờ. Chỉ người ĐƯỢC MỜI gọi được. */
export const deDeclineChallenge = (leagueId: string, opponentId: string) => {
  const endpoint = `/leagues/${leagueId}/de/challenge/${opponentId}/decline`;
  return callApi(endpoint, "POST", {}, "v1");
};

/* --------------------------- BXH WPA 9-Ball thế giới ---------------------
 * Do backend chụp lại từ wpapool.com; công khai nên không cần token.
 */
export const getWpaRankings = (board: "men" | "women") => {
  const endpoint = `/public/rankings/wpa/${board}`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};

/* ------------------------------ BXH FargoRate ---------------------------
 * Cùng kiểu với WPA: backend chụp lại từ fargorate.com rồi phục vụ bản chụp,
 * nên front office không bao giờ gọi thẳng sang trang nguồn.
 */
export const getFargoRankings = (board: "world" | "world-female") => {
  const endpoint = `/public/rankings/fargo/${board}`;
  return callApi(endpoint, "GET", {}, "v1", null, true);
};
/* =========================================================
   TIN TỨC / SỰ KIỆN
   -----------------------------------------------------------------
   Bài viết soạn ở backoffice, đọc qua API công khai - front office không ghi gì.
   Truyền token = true: callApi hiểu đó là "endpoint không bắt buộc đăng nhập",
   dùng token thật nếu có và không gửi header nếu chưa đăng nhập.
   ========================================================= */

export const getPublicNews = (filter: NewsFilterInterface = {}) => {
  const params: string[] = [];
  params.push(`limit=${filter.limit ?? 10}`);
  params.push(`page=${filter.page ?? 1}`);
  if (filter.type) {
    params.push(`type=${filter.type}`);
  }
  if (filter.category) {
    params.push(`category=${encodeURIComponent(filter.category)}`);
  }
  if (filter.featured) {
    params.push("featured=true");
  }
  if (filter.keyword) {
    params.push(`keyword=${encodeURIComponent(filter.keyword)}`);
  }

  return callApi(`/public/news?${params.join("&")}`, "GET", "", "v1", true);
};

/**
 * Các bài đang được ghim lên một khối của trang chủ, mới nhất trước.
 *
 * Ghim là công tắc trong backoffice (Nội dung → Tin tức), tối đa ba bài mỗi khối.
 * Trang chủ chỉ hiện bài được ghim chứ không rơi về "bài mới nhất" - làm vậy thì
 * công tắc ghim chẳng còn nghĩa gì.
 */
export const getPinnedNews = (type: NewsTypeValue, limit = 3) =>
  getPublicNews({ type, featured: true, limit });

export const getPublicNewsBySlug = (slug: string) =>
  callApi(`/public/news/${encodeURIComponent(slug)}`, "GET", "", "v1", true);

export const getPublicNewsCategories = () =>
  callApi("/public/news/categories", "GET", "", "v1", true);

/* ── Trang giới thiệu quán ─────────────────────────────────────────────── */

/**
 * Một quán kèm tiện ích, bảng giá và ưu đãi CÒN HIỆU LỰC.
 *
 * Backend đã lọc ưu đãi theo giờ máy chủ và sắp sẵn thứ tự mọi danh sách; front
 * office giữ nguyên những gì nhận được. Không cần đăng nhập.
 */
export const getPublicVenueById = (venueId: string) =>
  callApi(`/public/venues/${encodeURIComponent(venueId)}`, "GET", "", "v1", true);

/**
 * Các quán được gắn cờ nổi bật, cho khối trên trang chủ.
 *
 * `limit` bị backend chặn ở 24; xin nhiều hơn là bị từ chối chứ không phải cắt bớt.
 */
export const getFeaturedVenues = (limit = 6) =>
  callApi(`/public/venues/featured?limit=${limit}`, "GET", "", "v1", true);

/**
 * Danh sách quán cho trang /venue, quán nổi bật lên trước.
 *
 * `keyword` quét tên, slogan và địa chỉ - backend quyết định quét ở đâu, front
 * office chỉ gửi chữ người dùng gõ.
 */
export const getPublicVenues = (params: { page?: number; limit?: number; keyword?: string } = {}) => {
  const query: string[] = [
    `page=${params.page ?? 1}`,
    `limit=${params.limit ?? 12}`,
  ];
  if (params.keyword) {
    query.push(`keyword=${encodeURIComponent(params.keyword)}`);
  }

  return callApi(`/public/venues?${query.join("&")}`, "GET", "", "v1", true);
};
